import { useEffect, useState } from 'react';
import { api } from './api';
import { t } from './i18n';
import { CITIES as FALLBACK_CITIES } from './format';

/**
 * The city collection.
 *
 * Cities are curated server-side rather than derived from whatever has been
 * listed, so the tiles and every picker work on an empty marketplace. Fetched
 * once per page load and shared: the request is memoized at module level, so ten
 * components asking for cities make one call.
 */

export type City = {
  id: string;
  name: string;
  slug: string;
  region: string | null;
  lat: number;
  lng: number;
};

let pending: Promise<City[]> | null = null;

export function loadCities(): Promise<City[]> {
  pending ??= api
    .get<{ cities: City[] }>('/cities')
    .then((response) => response.cities)
    .catch(() => {
      // A failed lookup must not leave a city picker empty and unusable; fall
      // back to the built-in list and let the next page load try again.
      pending = null;
      return FALLBACK_CITIES.map((name) => ({
        id: name,
        name,
        slug: name.toLowerCase(),
        region: null,
        lat: 0,
        lng: 0,
      }));
    });
  return pending;
}

export function useCities(): City[] {
  const [cities, setCities] = useState<City[]>([]);
  useEffect(() => {
    let active = true;
    void loadCities().then((list) => {
      if (active) setCities(list);
    });
    return () => {
      active = false;
    };
  }, []);
  return cities;
}

/** Ready-made options for a <Select>, with an optional "all cities" entry. */
export function cityOptions(cities: City[], allLabel?: string) {
  const options = cities.map((city) => ({ value: city.name, label: t(city.name) }));
  return allLabel ? [{ value: '', label: allLabel }, ...options] : options;
}
